import { useState, useCallback } from 'react';
import { useReportUrlSync } from '../hooks/useReportUrlSync';
import { ReportFilters } from '../components/ui/ReportFilters';
import { KeyMetricsSection } from '../components/ui/KeyMetricsSection';
import {
  CategorySpendingComparisonChart,
  MonthlySpendingByRootCategoryChart,
  TransactionSizeDistributionChart,
  CalendarHeatmap,
} from '../components/charts';
import { TopTransactionsTable } from '../components/tables';
import { DayTransactionsModal } from '../components/ui/DayTransactionsModal';
import { BinTransactionsModal } from '../components/ui/BinTransactionsModal';
import { CategoryTransactionsModal } from '../components/ui/CategoryTransactionsModal';
import type { TransactionBin } from '../utils/transactionBins';
import type { CategorySpending } from '../hooks/useCategorySpendingComparison';

export function ReportesPage() {
  useReportUrlSync();

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedBin, setSelectedBin] = useState<TransactionBin | null>(null);
  const [selectedCategory, setSelectedCategory] =
    useState<CategorySpending | null>(null);

  const handleDayClick = useCallback((date: string) => {
    setSelectedDate(date);
  }, []);

  const handleCloseDayModal = useCallback(() => {
    setSelectedDate(null);
  }, []);

  const handleBinClick = useCallback((bin: TransactionBin) => {
    setSelectedBin(bin);
  }, []);

  const handleCloseBinModal = useCallback(() => {
    setSelectedBin(null);
  }, []);

  const handleCategoryClick = useCallback((category: CategorySpending) => {
    setSelectedCategory(category);
  }, []);

  const handleCloseCategoryModal = useCallback(() => {
    setSelectedCategory(null);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-brand-primary mb-6">
        Reportes
      </h1>

      <KeyMetricsSection />

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Actividad diaria
        </h2>
        <CalendarHeatmap onDayClick={handleDayClick} />
      </div>

      <ReportFilters />

      <div className="flex flex-col gap-6">
        <div className="bg-white rounded-lg shadow-md p-6 min-h-[300px]">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Tendencias de gasto
          </h2>
          <MonthlySpendingByRootCategoryChart height={300} />
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 min-h-[300px]">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Comparación por categoría
          </h2>
          <CategorySpendingComparisonChart
            height={300}
            onCategoryClick={handleCategoryClick}
          />
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 min-h-[300px]">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Distribución por monto
          </h2>
          <TransactionSizeDistributionChart
            height={300}
            onBinClick={handleBinClick}
          />
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Transacciones mas grandes
          </h2>
          <TopTransactionsTable />
        </div>
      </div>

      {selectedDate && (
        <DayTransactionsModal
          isOpen={true}
          onClose={handleCloseDayModal}
          date={selectedDate}
        />
      )}

      {selectedBin && (
        <BinTransactionsModal
          isOpen={true}
          onClose={handleCloseBinModal}
          binLabel={selectedBin.label}
          transactions={selectedBin.transactions}
        />
      )}

      {selectedCategory && (
        <CategoryTransactionsModal
          isOpen={true}
          onClose={handleCloseCategoryModal}
          categoryName={selectedCategory.categoryName}
          transactions={selectedCategory.transactions}
        />
      )}
    </div>
  );
}
